import { useEffect, useState } from 'react';
import {
  detectPlatform,
  isStandalone,
  subscribe,
  triggerInstallPrompt,
  type PwaPlatform,
} from '../lib/pwa-install';

// iOS Safari Share button icon — box with arrow going up out of the top.
// Matches the actual iOS UI so users recognize what to tap.
const IosShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="inline-block align-text-bottom mx-0.5"
    aria-hidden="true"
  >
    <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

type Props = {
  open: boolean;
  onClose: () => void;
  /** Brand name shown in the copy. Each brand's home page passes its own. */
  appName?: string;
};

export default function InstallPwaModal({ open, onClose, appName = 'this app' }: Props) {
  const [, force] = useState(0);
  const [platform, setPlatform] = useState<PwaPlatform>('unknown');

  useEffect(() => {
    const unsub = subscribe(() => force((n) => n + 1));
    return unsub;
  }, []);

  useEffect(() => {
    if (open) setPlatform(detectPlatform());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  if (isStandalone()) {
    // Already installed; close on next tick rather than render.
    queueMicrotask(onClose);
    return null;
  }

  const onInstall = async () => {
    const outcome = await triggerInstallPrompt();
    if (outcome !== 'unavailable') onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <i className="ri-download-2-line text-xl" />
            </div>
            <h2 className="text-xl font-bold">{modalTitle(platform, appName)}</h2>
          </div>
        </div>

        <div className="px-6 py-5 text-gray-700 text-sm leading-relaxed">
          {modalBody(platform, appName)}
        </div>

        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          {platform === 'installable' ? (
            <>
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-semibold text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Not now
              </button>
              <button
                onClick={onInstall}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-download-2-line" />
                Install
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function modalTitle(p: PwaPlatform, appName: string) {
  switch (p) {
    case 'installable': return `Install ${appName}`;
    case 'ios-safari': return 'Add to Home Screen';
    case 'ios-other': return 'Install via Safari';
    case 'samsung-internet': return `Install ${appName}`;
    case 'android-other': return `Install ${appName}`;
    case 'firefox-desktop': return 'Install not supported';
    default: return `Install ${appName}`;
  }
}

function modalBody(p: PwaPlatform, appName: string) {
  switch (p) {
    case 'installable':
      return (
        <p>
          Install {appName} for a faster, app-like experience with quick access from your home screen and offline support.
        </p>
      );
    case 'ios-safari':
      return (
        <div>
          <p className="mb-3">Install {appName} on your iPhone or iPad in three steps:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Tap the <IosShareIcon /> <strong>Share</strong> button at the bottom of Safari.
            </li>
            <li>
              Scroll and tap <strong>Add to Home Screen</strong>.
            </li>
            <li>
              Tap <strong>Add</strong> in the top right.
            </li>
          </ol>
        </div>
      );
    case 'ios-other':
      return (
        <p>
          On iPhone and iPad, apps can only be installed from Safari. Open this page in <strong>Safari</strong>, then tap <IosShareIcon /> Share → <strong>Add to Home Screen</strong>.
        </p>
      );
    case 'samsung-internet':
      return (
        <div>
          <p className="mb-3">To install {appName} from Samsung Internet:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Tap the menu <strong>☰</strong> at the bottom right of the browser.
            </li>
            <li>
              Tap <strong>Add page to</strong> → <strong>Home screen</strong> (or <strong>Install app</strong> if shown).
            </li>
            <li>
              Tap <strong>Add</strong> to confirm.
            </li>
          </ol>
          <p className="mt-3 text-gray-500">If a native install banner appears, you can use that instead.</p>
        </div>
      );
    case 'android-other':
      return (
        <div>
          <p className="mb-3">To install {appName} on Android:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Open your browser menu (⋮ in the top right).</li>
            <li>
              Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.
            </li>
            <li>Confirm the install.</li>
          </ol>
          <p className="mt-3 text-gray-500">Tip: Chrome offers the best install experience on Android.</p>
        </div>
      );
    case 'firefox-desktop':
      return (
        <p>
          Firefox desktop doesn't support installing web apps. To install {appName}, open this page in <strong>Chrome</strong>, <strong>Edge</strong>, or <strong>Brave</strong> and click Install when prompted.
        </p>
      );
    default:
      return (
        <p>
          Your browser doesn't expose an install option here. Try Chrome, Edge, or Brave for the best install experience.
        </p>
      );
  }
}
